import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDelete } from "components/ConfirmDialog/ConfirmDelete";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { useApi } from "providers";
import { useToasts } from "providers";
import { useTranslations } from "providers";
import { useCallback } from "react";

import { useBoard } from "../useBoard";

export const ConfirmDeleteBoard: React.FC = () => {
  const i18n = useTranslations();
  const toasts = useToasts();
  const { boardApi } = useApi();
  const { confirmDeleteBoard, setConfirmDeleteBoard, setBoardDeleting } = useBoard();
  const queryClient = useQueryClient();

  const isOpen = confirmDeleteBoard !== undefined;

  const handleDelete = useCallback(
    async (target?: ConfirmDeleteTarget) => {
      if (!target) return;

      setConfirmDeleteBoard(undefined);
      setBoardDeleting(target.id, true);

      try {
        await boardApi.deleteBoard(target.id);
        toasts.deleteSuccess(i18n.Board, target.title);
      } catch (error) {
        toasts.deleteFailure(i18n.Board, target.title, error);
      } finally {
        setBoardDeleting(target.id, false);
        queryClient.invalidateQueries({ queryKey: ["boards-list"] });
        // state cleared via setConfirmDeleteBoard
      }
    },
    [boardApi, queryClient, setConfirmDeleteBoard, setBoardDeleting, i18n, toasts]
  );

  const setIsOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        setConfirmDeleteBoard(undefined);
      }
    },
    [setConfirmDeleteBoard]
  );

  return (
    <ConfirmDelete
      entityName={i18n.Board}
      entityTitle={confirmDeleteBoard?.title}
      onAccept={() => handleDelete(confirmDeleteBoard)}
      onCancel={() => setConfirmDeleteBoard(undefined)}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
};
